import { useEffect } from 'react'
import mapboxgl from 'mapbox-gl'
import {
  createHazardsGeoJSON,
  getHazardTypeColor,
  getHazardFillOpacityExpression,
  isHazardLayer,
  formatHazardTooltip,
} from '../../../../molecules/AnalysisMap/utils/hazardLayerHelpers'
import { getLayerConfig } from '../../../../molecules/AnalysisMap/config'

interface UseHazardsLayersProps {
  map: React.MutableRefObject<mapboxgl.Map | null>
  mapLoaded: boolean
  popup: React.MutableRefObject<mapboxgl.Popup | null>
  styleLoadedCount: number
  otherMapOptions: any
}

/**
 * Hook to handle hazard zone polygon layers
 * Creates dynamic layers per hazard type
 * (flood, landslide, rockslide, avalanche)
 */
export const useHazardsLayers = ({
  map,
  mapLoaded,
  popup,
  styleLoadedCount,
  otherMapOptions,
}: UseHazardsLayersProps) => {
  useEffect(() => {
    if (!map.current || !mapLoaded) return

    const activeSection = (otherMapOptions as any).activeSection
    const isHazardsSection = activeSection === 'hazards'
    const hazardZones = (otherMapOptions as any).sectionData?.hazardZones
    const layerStates = (otherMapOptions as any).demographicsLayerStates

    if (!isHazardsSection || !hazardZones || hazardZones.length === 0) {
      // Cleanup existing hazard layers
      let existing: string[] = []
      try {
        const style = map.current.getStyle()
        if (style?.layers) {
          existing = style.layers
            .filter((l) => isHazardLayer(l.id))
            .map((l) => l.id)
        }
      } catch {
        return
      }

      existing.forEach((layerId) => {
        if (map.current!.getLayer(layerId)) {
          try {
            map.current!.removeLayer(layerId)
          } catch {
            // ignore
          }
        }
      })

      existing.forEach((layerId) => {
        const sourceId = `${layerId}-source`
        if (map.current!.getSource(sourceId)) {
          try {
            map.current!.removeSource(sourceId)
          } catch {
            // ignore
          }
        }
      })

      return
    }

    // Group zones by hazard type
    const typeMap = new Map<string, { name: string; zones: any[] }>()
    for (const zone of hazardZones) {
      const key = zone.type || zone.name
      if (!typeMap.has(key)) {
        typeMap.set(key, { name: key, zones: [] })
      }
      typeMap.get(key)!.zones.push(zone)
    }

    typeMap.forEach(({ zones }, typeKey) => {
      const layerId = `hazards-${typeKey}`
      const sourceId = `${layerId}-source`
      const geoJson = createHazardsGeoJSON(zones)
      const fillColor = getHazardTypeColor(typeKey)

      const layerConfig =
        getLayerConfig(layerId) || getLayerConfig('restrictions-base')
      const layerState = layerStates?.[layerId] || {
        enabled: true,
        opacity: layerConfig?.defaultOpacity || 30,
      }

      // Add or update source
      if (!map.current!.getSource(sourceId)) {
        map.current!.addSource(sourceId, {
          type: 'geojson',
          data: geoJson,
          generateId: false,
        })
      } else {
        const source = map.current!.getSource(
          sourceId
        ) as mapboxgl.GeoJSONSource
        source.setData(geoJson)
      }

      // Add or update layers
      if (!map.current!.getLayer(layerId)) {
        // Fill layer
        map.current!.addLayer({
          id: layerId,
          type: 'fill',
          source: sourceId,
          layout: {
            visibility: layerState.enabled ? 'visible' : 'none',
          },
          paint: {
            'fill-color': fillColor,
            'fill-opacity': getHazardFillOpacityExpression(layerState.opacity),
          },
        })

        // Outline layer
        const outlineId = `${layerId}-outline`
        if (!map.current!.getLayer(outlineId)) {
          map.current!.addLayer({
            id: outlineId,
            type: 'line',
            source: sourceId,
            layout: {
              visibility: layerState.enabled ? 'visible' : 'none',
            },
            paint: {
              'line-color': fillColor,
              'line-width': 0.5,
              'line-opacity': 0.6,
            },
          })
        }

        // Hover layer
        const hoverId = `${layerId}-hover`
        if (!map.current!.getLayer(hoverId)) {
          map.current!.addLayer({
            id: hoverId,
            type: 'line',
            source: sourceId,
            layout: {
              visibility: layerState.enabled ? 'visible' : 'none',
            },
            paint: {
              'line-color': fillColor,
              'line-width': 4,
              'line-opacity': [
                'case',
                ['boolean', ['feature-state', 'hover'], false],
                1,
                0,
              ] as any,
            },
          })
        }
      } else {
        // Update existing
        map.current!.setLayoutProperty(
          layerId,
          'visibility',
          layerState.enabled ? 'visible' : 'none'
        )
        map.current!.setPaintProperty(
          layerId,
          'fill-opacity',
          getHazardFillOpacityExpression(layerState.opacity)
        )

        const outlineId = `${layerId}-outline`
        if (map.current!.getLayer(outlineId)) {
          map.current!.setLayoutProperty(
            outlineId,
            'visibility',
            layerState.enabled ? 'visible' : 'none'
          )
        }

        const hoverId = `${layerId}-hover`
        if (map.current!.getLayer(hoverId)) {
          map.current!.setLayoutProperty(
            hoverId,
            'visibility',
            layerState.enabled ? 'visible' : 'none'
          )
        }
      }
    })

    // Cleanup removed types
    const currentIds = Array.from(typeMap.keys()).map((k) => `hazards-${k}`)
    let existingLayers: string[] = []
    try {
      const style = map.current.getStyle()
      if (style?.layers) {
        existingLayers = style.layers
          .filter(
            (l) =>
              l.id.startsWith('hazards-') &&
              !l.id.endsWith('-outline') &&
              !l.id.endsWith('-hover')
          )
          .map((l) => l.id)
      }
    } catch {
      return
    }

    existingLayers.forEach((layerId) => {
      if (!currentIds.includes(layerId)) {
        ;[layerId, `${layerId}-outline`, `${layerId}-hover`].forEach((id) => {
          if (map.current!.getLayer(id)) {
            map.current!.removeLayer(id)
          }
        })
        const sourceId = `${layerId}-source`
        if (map.current!.getSource(sourceId)) {
          map.current!.removeSource(sourceId)
        }
      }
    })
  }, [mapLoaded, styleLoadedCount, otherMapOptions])

  // Hover tooltip handling
  useEffect(() => {
    if (!map.current || !mapLoaded) return

    const activeSection = (otherMapOptions as any).activeSection
    if (activeSection !== 'hazards') return

    let hoveredFeatureId: number | null = null
    let hoveredSourceId: string | null = null

    const onMouseMove = (e: any) => {
      if (!map.current || !e.features?.length) return

      const feature = e.features[0]
      const sourceId = feature.source

      // Clear previous hover
      if (hoveredFeatureId !== null && hoveredSourceId) {
        map.current.setFeatureState(
          {
            source: hoveredSourceId,
            id: hoveredFeatureId,
          },
          { hover: false }
        )
      }

      hoveredFeatureId = feature.id
      hoveredSourceId = sourceId
      map.current.setFeatureState(
        { source: sourceId, id: feature.id },
        { hover: true }
      )
      map.current.getCanvas().style.cursor = 'pointer'

      if (popup.current) {
        popup.current
          .setLngLat(e.lngLat)
          .setHTML(formatHazardTooltip(feature.properties))
          .addTo(map.current)
      }
    }

    const onMouseLeave = () => {
      if (!map.current || hoveredFeatureId === null || !hoveredSourceId) return

      map.current.setFeatureState(
        {
          source: hoveredSourceId,
          id: hoveredFeatureId,
        },
        { hover: false }
      )
      hoveredFeatureId = null
      hoveredSourceId = null
      map.current.getCanvas().style.cursor = ''

      if (popup.current) {
        popup.current.remove()
      }
    }

    // Attach listeners to all hazard fill layers
    const style = map.current.getStyle()
    const hazardFillLayers =
      style?.layers
        ?.filter(
          (l) =>
            l.id.startsWith('hazards-') &&
            !l.id.endsWith('-outline') &&
            !l.id.endsWith('-hover')
        )
        .map((l) => l.id) || []

    hazardFillLayers.forEach((layerId) => {
      map.current!.on('mousemove', layerId, onMouseMove)
      map.current!.on('mouseleave', layerId, onMouseLeave)
    })

    return () => {
      hazardFillLayers.forEach((layerId) => {
        if (map.current) {
          map.current.off('mousemove', layerId, onMouseMove)
          map.current.off('mouseleave', layerId, onMouseLeave)
        }
      })
    }
  }, [mapLoaded, otherMapOptions])
}
